import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';

import { texts } from 'utils/languages/fr';
import { countries, situations, activitySectors, matrimonialRegimes } from 'utils/formData';

import {
    CheckboxLabel,
    CustomButton,
    CustomDatePicker,
    CustomPhoneField,
    CustomSelect,
    CustomTextField,
    RadioLabel,
} from 'components';
import GeneralInfoForm from '../forms/GeneralInfoForm';
import FiscalAddressForm from '../forms/FiscalAddressForm';
import PoliticallyExposedForm from '../forms/PoliticallyExposedForm';
import ConditionsForm from '../forms/ConditionsForm';
import FaxForm from '../forms/FaxForm';
import SituationFprmFCPRPP from '../forms/SituationFormFCPRPP';
import SituationFormFPCIPM from '../forms/SituationFormFPCIPM';
import FiscalSituationFormFCPRPP from '../forms/FiscalSituationFormFCPRPP';
import FiscalSituationFormFPCIPM from '../forms/FiscalSituationFormFPCIPM';
import SituationFormFPCIPP from '../forms/SituationFormFPCIPP';
import CorporateForm from '../forms/CorporateForm';
import SubscriberDeclarationForm from '../forms/SubscriberDeclarationForm';

import * as icons from 'mdi-material-ui';
import { FormControl, IconButton, MenuItem, RadioGroup } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    bold: {
        fontWeight: 600,
    },
    separator: {
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
}));

const renderOption = opt => (
    <MenuItem key={opt.id} value={opt.id}>
        {opt.name}
    </MenuItem>
);

export const MemoizedIndexedCountriesSelect = React.memo(
    ({ className, name, value, index, onChange }) => {
        const handleChange = useCallback(e => onChange(e, index), [index, onChange]);

        const RenderedCountries = useMemo(() => countries.map(renderOption), []);

        return (
            <CustomSelect
                name={name}
                inputStyle={className}
                label={texts.COUNTRY}
                value={value}
                onChange={handleChange}
            >
                {RenderedCountries}
            </CustomSelect>
        );
    }
);

export const MemoizedIndexedTextField = React.memo(
    ({ className, name, label, value, index, onChange }) => {
        const handleChange = useCallback(e => onChange(e, index), [onChange, index]);

        return (
            <CustomTextField
                name={name}
                inputStyle={className}
                label={label}
                value={value}
                onChange={handleChange}
            />
        );
    }
);

export const MemoizedIndexedFormControlLabel = React.memo(
    ({ index, label, name, value, checked, onChange, className }) => {
        const handleChange = useCallback(e => onChange(e, index), [onChange, index]);

        return (
            <CheckboxLabel
                classes={{ root: className }}
                label={label}
                checked={checked}
                value={value}
                onChange={handleChange}
                name={name}
            />
        );
    }
);

export const MemoizedCountriesSelect = React.memo(
    ({ inputStyle, name, label, value, onChange, error, ...rest }) => {
        const mappedCountries = useMemo(() => countries.map(renderOption), []);

        return (
            <CustomSelect
                name={name}
                inputStyle={inputStyle}
                label={label}
                value={value}
                onChange={onChange}
                error={error}
                {...rest}
            >
                {mappedCountries}
            </CustomSelect>
        );
    }
);

export const MemoizedSituationsSelect = React.memo(
    ({ inputStyle, name, label, value, onChange, error, selected }) => {
        const renderCivilityOption = opt => (
            <MenuItem key={opt.id} value={opt.name}>
                {opt.name}
            </MenuItem>
        );

        const RenderedSituations = useMemo(() => situations.map(renderCivilityOption), []);

        return (
            <CustomSelect
                name={name}
                inputStyle={inputStyle}
                label={label}
                value={value}
                onChange={onChange}
                error={error}
                selected={selected}
            >
                {RenderedSituations}
            </CustomSelect>
        );
    }
);

export const MemoizedMatrimonialRegimeSelect = React.memo(
    ({ inputStyle, name, label, value, onChange, error, selected }) => {
        const renderCivilityOption = opt => (
            <MenuItem key={opt.id} value={opt.name}>
                {opt.name}
            </MenuItem>
        );

        const RenderedRegimes = useMemo(() => matrimonialRegimes.map(renderCivilityOption), []);

        return (
            <CustomSelect
                name={name}
                inputStyle={inputStyle}
                label={label}
                value={value}
                onChange={onChange}
                error={error}
                selected={selected}
            >
                {RenderedRegimes}
            </CustomSelect>
        );
    }
);

export const MemoizedActivitySectorsSelect = React.memo(
    ({ inputStyle, name, label, value, onChange, error, selected }) => {
        const classes = useStyles();
        const renderCivilityOption = useCallback(
            (opt, i) => (
                <MenuItem
                    key={opt.id}
                    value={opt.name}
                    className={clsx({
                        [classes.bold]: i < 4,
                        [classes.separator]: i === 3 || i === activitySectors.length - 2,
                    })}
                >
                    {opt.name}
                </MenuItem>
            ),
            [classes]
        );

        const RenderedActivities = useMemo(
            () => activitySectors.map(renderCivilityOption),
            [renderCivilityOption]
        );

        return (
            <CustomSelect
                name={name}
                inputStyle={inputStyle}
                label={label}
                value={value}
                onChange={onChange}
                error={error}
                selected={selected}
            >
                {RenderedActivities}
            </CustomSelect>
        );
    }
);

export const MemoizedTextField = React.memo(
    ({ inputStyle, name, label, value, onChange, error, showError, ...rest }) => {
        return (
            <CustomTextField
                name={name}
                inputStyle={inputStyle}
                label={label}
                value={value}
                onChange={onChange}
                error={error}
                showError={showError}
                {...rest}
            />
        );
    }
);

export const MemoizedPhoneField = React.memo(
    ({ name, label, value, onChange, error, showError, ...rest }) => {
        return (
            <CustomPhoneField
                label={label}
                value={value}
                onChange={onChange}
                error={error}
                showError={showError}
                {...rest}
            />
        );
    }
);

export const MemoizedDatePicker = React.memo(
    ({ inputStyle, name, label, value, onChange, error, showError, ...rest }) => {
        return (
            <CustomDatePicker
                disableFuture
                name={name}
                inputStyle={inputStyle}
                label={label}
                value={value}
                onChange={onChange}
                error={error}
                showError={showError}
                {...rest}
            />
        );
    }
);

export const MemoizedIndexedButtonsActions = React.memo(
    ({ index, className, onRemove, onAdd, isLast }) => {
        const handleRemove = useCallback(e => onRemove(e, index), [onRemove, index]);

        return (
            <div className={className}>
                <IconButton size="small" onClick={handleRemove}>
                    <icons.Delete />
                </IconButton>
                {isLast && (
                    <IconButton color="primary" size="small" onClick={onAdd}>
                        <icons.PlusCircle />
                    </IconButton>
                )}
            </div>
        );
    }
);

export const MemoizedRadioGroup = React.memo(
    ({ value, name, onChange, yesValue, noValue, yesLabel, noLabel, row = false }) => {
        return (
            <FormControl component="fieldset">
                <RadioGroup name={name} value={value} onChange={onChange} row={row}>
                    <RadioLabel value={yesValue} label={yesLabel} />
                    <RadioLabel value={noValue} label={noLabel} />
                </RadioGroup>
            </FormControl>
        );
    }
);

export const MemoizedButton = React.memo(({ className, label, onClick, ...rest }) => {
    return (
        <CustomButton className={className} label={label} onClick={onClick} noMargin {...rest} />
    );
});

export const MemoizedGeneralInfoForm = React.memo(
    ({
        errors,
        isPhoneVerified,
        generalInfo,
        onChange,
        tunnelErrors,
        displayAllFields,
        showIdentityInfo,
        ...rest
    }) => {
        return (
            <GeneralInfoForm
                generalInfo={generalInfo}
                onChange={onChange}
                errors={errors}
                isPhoneVerified={isPhoneVerified}
                tunnelErrors={tunnelErrors}
                displayAllFields={displayAllFields}
                showIdentityInfo={showIdentityInfo}
                {...rest}
            />
        );
    }
);

export const MemoizedFaxForm = React.memo(({ fax, onChange }) => {
    return <FaxForm fax={fax} onChange={onChange} />;
});

export const MemoizedFiscalAddressForm = React.memo(
    ({
        label,
        fiscalAddress,
        hasDifferentFiscalAddress,
        onChange,
        onToggle,
        errors,
        tunnelErrors,
    }) => {
        return (
            <FiscalAddressForm
                label={label}
                fiscalAddress={fiscalAddress}
                hasDifferentFiscalAddress={hasDifferentFiscalAddress}
                onChange={onChange}
                onToggle={onToggle}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
        );
    }
);

export const MemoizedSituationFormFCPRPP = React.memo(
    ({ situation, onChange, errors, tunnelErrors }) => {
        return (
            <SituationFprmFCPRPP
                situation={situation}
                onChange={onChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
        );
    }
);

export const MemoizedSituationFormFPCIPM = React.memo(
    ({ situation, onChange, errors, tunnelErrors }) => {
        return (
            <SituationFormFPCIPM
                situation={situation}
                onChange={onChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
        );
    }
);

export const MemoizedSituationFormFPCIPP = React.memo(
    ({ situation, onChange, errors, tunnelErrors }) => {
        return (
            <SituationFormFPCIPP
                situation={situation}
                onChange={onChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
        );
    }
);

export const MemoizedFiscalSituationFormFCPRPP = React.memo(
    ({
        errors,
        tunnelErrors,
        fiscalSituation,
        fiscalObligations,
        onChange,
        onAddLine,
        onRemoveLine,
        onUpdateLine,
    }) => {
        return (
            <FiscalSituationFormFCPRPP
                fiscalSituation={fiscalSituation}
                fiscalObligations={fiscalObligations}
                onChange={onChange}
                onAddLine={onAddLine}
                onRemoveLine={onRemoveLine}
                onUpdateLine={onUpdateLine}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
        );
    }
);

export const MemoizedFiscalSituationFormFPCIPM = React.memo(
    ({ errors, fiscalSituation, onChange }) => {
        return (
            <FiscalSituationFormFPCIPM
                fiscalSituation={fiscalSituation}
                onChange={onChange}
                errors={errors}
            />
        );
    }
);

export const MemoizedPoliticallyExposedForm = React.memo(
    ({ politicallyExposed, onChange, onChangeReason }) => {
        return (
            <PoliticallyExposedForm
                politicallyExposed={politicallyExposed}
                onChange={onChange}
                onChangeReason={onChangeReason}
            />
        );
    }
);

export const MemoizedConditionsForm = React.memo(({ conditions, onChange }) => {
    return <ConditionsForm conditions={conditions} onChange={onChange} />;
});

export const MemoizedCorporateForm = React.memo(
    ({
        corporateInfo,
        onChange,
        onFileUpload,
        onCorporateAddressChange,
        onCorporatePostalAddressChange,
        errors,
        tunnelErrors,
        hasDifferentPostalAddress,
        toggleCorporatePostalAddress,
        displayAllFields,
        fileSizeError,
        fileTypeError,
    }) => {
        return (
            <CorporateForm
                corporateInfo={corporateInfo}
                onChange={onChange}
                onFileUpload={onFileUpload}
                onCorporateAddressChange={onCorporateAddressChange}
                onCorporatePostalAddressChange={onCorporatePostalAddressChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
                hasDifferentPostalAddress={hasDifferentPostalAddress}
                toggleCorporatePostalAddress={toggleCorporatePostalAddress}
                displayAllFields={displayAllFields}
                fileSizeError={fileSizeError}
                fileTypeError={fileTypeError}
            />
        );
    }
);

export const MemoizedSubscriberDeclarationForm = React.memo(
    ({ ownAccountAgreement, onChange, errors, ...rest }) => {
        return (
            <SubscriberDeclarationForm
                ownAccountAgreement={ownAccountAgreement}
                onChange={onChange}
                errors={errors}
                {...rest}
            />
        );
    }
);
